// LinkedIn Insight Tag conversion tracker.
//
// The base lintrk script is loaded by GTM (see public/index.html). This
// helper fires a conversion event for a given conversion_id, with two
// guards:
//   1. Production only — staging/dev runs never count against the
//      LinkedIn campaign data.
//   2. Per-browser dedupe via localStorage — a user who triggers the
//      same conversion event multiple times only registers once.

declare global {
    interface Window {
        lintrk?: (action: string, payload: { conversion_id: number }) => void;
    }
}

const LINTRK_FIRED_KEY_PREFIX = "tdc:lintrkFired:";

function isProduction(): boolean {
    return process.env.REACT_APP_ENV === "production";
}

function hasFired(dedupeKey: string): boolean {
    try {
        return window.localStorage.getItem(LINTRK_FIRED_KEY_PREFIX + dedupeKey) === "1";
    } catch {
        // localStorage can throw in private mode / quota exceeded. Treat as
        // "not fired" — at worst we'll fire once per session.
        return false;
    }
}

function markFired(dedupeKey: string) {
    try {
        window.localStorage.setItem(LINTRK_FIRED_KEY_PREFIX + dedupeKey, "1");
    } catch {
        // ignore — see above
    }
}

/**
 * Fire a LinkedIn conversion exactly once per browser, only in production.
 * Safe to call from anywhere: never throws, no-ops when the Insight Tag
 * isn't on the page yet, the env isn't production, or the dedupe key has
 * already fired.
 *
 * @param conversionId LinkedIn conversion_id from the campaign manager
 * @param dedupeKey    Short identifier (e.g. "pdpAudit", "register") used
 *                     as the localStorage key suffix
 */
export function fireLinkedInConversion(conversionId: number, dedupeKey: string) {
    if (typeof window === "undefined") return;
    if (!isProduction()) return;
    if (hasFired(dedupeKey)) return;
    if (typeof window.lintrk !== "function") return;
    try {
        window.lintrk("track", { conversion_id: conversionId });
        markFired(dedupeKey);
    } catch {
        // Never let a tracker error block the calling code's success path.
    }
}
